import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr, inrShort } from "@/lib/format";
import { toast } from "sonner";
import { FileDown, Link2, Plus, X, TrendingDown, Pencil, Trash2, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BM_STATUS_TONE = {
  pending: "bg-anvi-100 text-anvi-700",
  due: "bg-amber-50 text-amber-800",
  invoice_drafted: "bg-blue-50 text-blue-700",
  invoiced: "bg-purple-50 text-purple-800",
  paid: "bg-emerald-50 text-emerald-700",
};
const BM_LABELS = {
  pending: "Pending",
  due: "Due",
  invoice_drafted: "Invoice Drafted",
  invoiced: "Invoiced",
  paid: "Paid",
};

function LinkModal({ bm, siteMilestones, onClose, onLink }) {
  const [sel, setSel] = useState(bm.linked_site_milestone_id || "");
  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="link-milestone-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-anvi-200">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Link to Site Milestone</div>
            <div className="font-heading text-xl text-anvi-900 mt-0.5">{bm.label}</div>
          </div>
          <button onClick={onClose} className="text-anvi-500 hover:text-anvi-900 p-1"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-anvi-500">When this site milestone is marked done, an invoice for {inr(bm.amount)} will be auto-drafted (not sent — requires human review).</p>
          <div className="space-y-1.5">
            {siteMilestones.map((s) => (
              <button key={s.id} onClick={() => setSel(s.id)} data-testid={`link-choice-${s.id}`}
                      className={`w-full text-left border rounded-md px-3 py-2.5 flex justify-between items-center transition-colors ${sel === s.id ? "border-anvi-terracotta bg-anvi-terracotta/5" : "border-anvi-200 hover:border-anvi-300"}`}>
                <div>
                  <div className="text-sm font-medium text-anvi-900">{s.name}</div>
                  <div className="text-xs text-anvi-500">Current status: {s.status.replace("_", " ")}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
            <button onClick={() => onLink(sel)} disabled={!sel} data-testid="submit-link"
                    className="px-4 py-2 text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark rounded-md disabled:opacity-60">
              Link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreditNoteModal({ projectId, onClose, onCreated }) {
  const [amount, setAmount] = useState(5000);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Reason required");
    setSaving(true);
    try {
      await api.creditNoteCreate({ project_id: projectId, amount: Number(amount), reason });
      onCreated();
      onClose();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="credit-note-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-anvi-200">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Credit Note</div>
            <div className="font-heading text-xl text-anvi-900 mt-0.5">Issue adjustment</div>
          </div>
          <button onClick={onClose} className="text-anvi-500 hover:text-anvi-900 p-1"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Amount (₹)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cn-amount"
                   className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:border-anvi-terracotta" />
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Reason</label>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cn-reason"
                      placeholder="e.g. Scope-change discount for granite upgrade removal"
                      className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md">Cancel</button>
            <button onClick={submit} disabled={saving} data-testid="submit-cn"
                    className="px-4 py-2 text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark rounded-md disabled:opacity-60">
              {saving ? "Saving…" : "Issue Credit Note"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreditNoteRow({ cn, canManage, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(cn.amount);
  const [reason, setReason] = useState(cn.reason);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setSaving(true);
    try {
      await api.creditNoteUpdate(cn.id, { amount: Number(amount), reason });
      toast.success("Credit note updated");
      setEditing(false);
      onChanged();
    } catch { toast.error("Could not update"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete credit note ${cn.cn_number} (${inr(cn.amount)})? This cannot be undone.`)) return;
    try {
      await api.creditNoteDelete(cn.id);
      toast.success("Credit note deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  if (editing) {
    return (
      <tr className="border-b border-anvi-100 bg-anvi-50/60" data-testid={`credit-note-edit-${cn.id}`}>
        <td className="px-4 py-2 font-mono text-xs text-anvi-700">{cn.cn_number}</td>
        <td className="px-4 py-2 text-xs text-anvi-500">{cn.issued_at ? new Date(cn.issued_at).toLocaleDateString("en-IN") : "—"}</td>
        <td className="px-4 py-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`cn-edit-reason-${cn.id}`}
                 className="w-full px-2 py-1 text-sm border border-anvi-200 rounded-sm focus:outline-none focus:border-anvi-terracotta" />
        </td>
        <td className="px-4 py-2">
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={`cn-edit-amount-${cn.id}`}
                 className="w-full px-2 py-1 text-sm text-right tabular-nums border border-anvi-200 rounded-sm focus:outline-none focus:border-anvi-terracotta" />
        </td>
        <td className="px-4 py-2 text-right">
          <div className="flex items-center justify-end gap-1">
            <button onClick={save} disabled={saving} title="Save" data-testid={`cn-save-${cn.id}`} className="text-emerald-600 hover:text-emerald-800 p-1">
              <Save size={13} strokeWidth={1.5} />
            </button>
            <button onClick={() => { setEditing(false); setAmount(cn.amount); setReason(cn.reason); }} title="Cancel" className="text-anvi-500 hover:text-anvi-900 p-1">
              <X size={13} strokeWidth={1.5} />
            </button>
          </div>
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-b border-anvi-100" data-testid={`credit-note-row-${cn.id}`}>
      <td className="px-4 py-3 font-mono text-xs text-anvi-700">{cn.cn_number}</td>
      <td className="px-4 py-3 text-xs text-anvi-500 tabular-nums">{cn.issued_at ? new Date(cn.issued_at).toLocaleDateString("en-IN") : "—"}</td>
      <td className="px-4 py-3 text-sm text-anvi-900">{cn.reason}</td>
      <td className="px-4 py-3 text-sm text-right tabular-nums text-emerald-700 font-medium">{inr(cn.amount)}</td>
      <td className="px-4 py-3 text-right">
        {canManage && (
          <div className="flex items-center justify-end gap-1">
            <button onClick={() => setEditing(true)} title="Edit" data-testid={`cn-edit-${cn.id}`} className="text-anvi-500 hover:text-anvi-terracotta p-1">
              <Pencil size={13} strokeWidth={1.5} />
            </button>
            <button onClick={remove} title="Delete" data-testid={`cn-delete-${cn.id}`} className="text-anvi-400 hover:text-red-600 p-1">
              <Trash2 size={13} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function BillingPanel({ projectId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const [showCN, setShowCN] = useState(false);
  const canManageCN = user && (user.role === "Owner" || user.role === "Admin" || user.role === "Accountant");

  const load = () => api.billing(projectId).then((r) => setData(r.data));
  useEffect(() => { load(); }, [projectId]);

  const link = async (siteMilestoneId) => {
    try {
      await api.billingMilestoneLink(linkTarget.id, siteMilestoneId);
      toast.success("Linked to site milestone");
      setLinkTarget(null);
      load();
    } catch { toast.error("Link failed"); }
  };

  const openStatement = () => window.open(api.billingStatementUrl(projectId), "_blank");

  if (!data) return <div className="text-anvi-500">Loading billing…</div>;

  const collectionPct = data.invoiced ? Math.min(100, Math.round((data.collected / data.invoiced) * 100)) : 0;
  const siteMilestoneMap = Object.fromEntries((data.site_milestones || []).map((m) => [m.id, m]));

  return (
    <div className="space-y-6" data-testid="billing-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-anvi-200 rounded-md p-5">
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Contract Value</div>
          <div className="font-heading text-2xl text-anvi-900 tabular-nums mt-1" data-testid="billing-contract">{inrShort(data.grand_total)}</div>
          <div className="text-xs text-anvi-500 mt-1">{data.approved_tier} package</div>
        </div>
        <div className="bg-white border border-anvi-200 rounded-md p-5">
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Invoiced</div>
          <div className="font-heading text-2xl text-anvi-900 tabular-nums mt-1" data-testid="billing-invoiced">{inrShort(data.invoiced)}</div>
        </div>
        <div className="bg-white border border-anvi-200 rounded-md p-5">
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Collected</div>
          <div className="font-heading text-2xl text-emerald-700 tabular-nums mt-1" data-testid="billing-collected">{inrShort(data.collected)}</div>
          <div className="text-xs text-anvi-500 mt-1">{collectionPct}% of invoiced</div>
        </div>
        <div className="bg-anvi-terracotta text-white rounded-md p-5">
          <div className="text-xs tracking-[0.15em] uppercase text-white/80">Outstanding</div>
          <div className="font-heading text-2xl tabular-nums mt-1" data-testid="billing-outstanding">{inrShort(data.outstanding)}</div>
          {data.credits_total > 0 && (
            <div className="text-xs text-white/80 mt-1 flex items-center gap-1"><TrendingDown size={10} /> {inr(data.credits_total)} in credit notes</div>
          )}
          {data.invoiced > data.grand_total && (
            <div className="text-xs text-white/80 mt-1 italic">Invoiced &gt; contract · variations applied</div>
          )}
        </div>
      </div>

      <div className="bg-white border border-anvi-200 rounded-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-anvi-200">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Billing Milestones</div>
            <div className="font-heading text-lg text-anvi-900 mt-0.5">{data.approved_tier} payment schedule</div>
          </div>
          <div className="flex gap-2">
            {canManageCN && (
              <button onClick={() => setShowCN(true)} data-testid="new-credit-note-btn"
                      className="text-xs border border-anvi-200 hover:border-anvi-terracotta text-anvi-700 px-3 py-1.5 rounded-md flex items-center gap-1.5">
                <Plus size={12} strokeWidth={1.5} /> Credit Note
              </button>
            )}
            <button onClick={openStatement} data-testid="download-statement-btn"
                    className="text-xs bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white px-3 py-1.5 rounded-md flex items-center gap-1.5">
              <FileDown size={12} strokeWidth={1.5} /> Statement PDF
            </button>
          </div>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 border-b border-anvi-200 bg-anvi-50/50">
              <th className="px-4 py-3 text-left font-medium">#</th>
              <th className="px-4 py-3 text-left font-medium">Milestone</th>
              <th className="px-4 py-3 text-right font-medium">%</th>
              <th className="px-4 py-3 text-right font-medium">Amount</th>
              <th className="px-4 py-3 text-left font-medium">Linked Site Milestone</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.billing_milestones.map((bm) => {
              const linkedSite = siteMilestoneMap[bm.linked_site_milestone_id];
              return (
                <tr key={bm.id} className="border-b border-anvi-100" data-testid={`billing-milestone-${bm.id}`}>
                  <td className="px-4 py-3.5 text-anvi-500 tabular-nums">{bm.sequence + 1}</td>
                  <td className="px-4 py-3.5 text-sm text-anvi-900 font-medium">{bm.label}</td>
                  <td className="px-4 py-3.5 text-sm text-right text-anvi-500 tabular-nums">{bm.pct}%</td>
                  <td className="px-4 py-3.5 text-sm text-right font-medium tabular-nums text-anvi-900">{inr(bm.amount)}</td>
                  <td className="px-4 py-3.5 text-sm text-anvi-500">
                    {linkedSite ? (
                      <span className="flex items-center gap-1.5">
                        <Link2 size={11} strokeWidth={1.5} className="text-anvi-terracotta" /> {linkedSite.name}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-anvi-100">{linkedSite.status}</span>
                      </span>
                    ) : (
                      <span className="text-anvi-400 italic">Not linked</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${BM_STATUS_TONE[bm.status]}`}>{BM_LABELS[bm.status] || bm.status}</span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <button onClick={() => setLinkTarget(bm)} data-testid={`link-bm-${bm.id}`}
                            className="text-xs text-anvi-terracotta hover:bg-anvi-terracotta/10 px-2 py-1 rounded-md">
                      {linkedSite ? "Re-link" : "Link"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-anvi-200 rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-anvi-200 flex items-center justify-between">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Credit Notes</div>
            <div className="font-heading text-lg text-anvi-900 mt-0.5">{data.credit_notes.length} issued · {inr(data.credits_total)} total</div>
          </div>
          <button onClick={() => setShowCN(true)} data-testid="new-credit-note-btn-bottom"
                  disabled={!canManageCN}
                  className="text-xs border border-anvi-200 hover:border-anvi-terracotta text-anvi-700 px-3 py-1.5 rounded-md flex items-center gap-1.5 disabled:opacity-40 disabled:hover:border-anvi-200 disabled:cursor-not-allowed"
                  style={{display: canManageCN ? "flex" : "none"}}>
            <Plus size={12} strokeWidth={1.5} /> New Credit Note
          </button>
        </div>
        {data.credit_notes.length === 0 ? (
          <div className="p-6 text-center text-sm text-anvi-500" data-testid="no-credit-notes">No credit notes yet. Issue one when a scope-change discount is needed.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 border-b border-anvi-200 bg-anvi-50/50">
                <th className="px-4 py-3 text-left font-medium">CN #</th>
                <th className="px-4 py-3 text-left font-medium">Issued</th>
                <th className="px-4 py-3 text-left font-medium">Reason</th>
                <th className="px-4 py-3 text-right font-medium">Amount</th>
                <th className="px-4 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {data.credit_notes.map((cn) => (
                <CreditNoteRow key={cn.id} cn={cn} canManage={canManageCN} onChanged={load} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-anvi-200 rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-anvi-200">
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Ledger</div>
          <div className="font-heading text-lg text-anvi-900 mt-0.5">All transactions</div>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 border-b border-anvi-200 bg-anvi-50/50">
              <th className="px-4 py-3 text-left font-medium">Date</th>
              <th className="px-4 py-3 text-left font-medium">Reference</th>
              <th className="px-4 py-3 text-left font-medium">Description</th>
              <th className="px-4 py-3 text-right font-medium">Debit</th>
              <th className="px-4 py-3 text-right font-medium">Credit</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.ledger.map((r) => (
              <tr key={`${r.at}-${r.reference || ""}-${r.type}`} className="border-b border-anvi-100" data-testid={`ledger-row-${r.at || ""}-${r.reference || ""}`}>
                <td className="px-4 py-3 text-xs text-anvi-500 tabular-nums">{r.at ? new Date(r.at).toLocaleDateString("en-IN") : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-anvi-700">{r.reference}</td>
                <td className="px-4 py-3 text-sm text-anvi-900">{r.description}</td>
                <td className="px-4 py-3 text-sm text-right tabular-nums text-anvi-900">{r.debit ? inr(r.debit) : "—"}</td>
                <td className="px-4 py-3 text-sm text-right tabular-nums text-emerald-700">{r.credit ? inr(r.credit) : "—"}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider bg-anvi-100 text-anvi-700">{r.status}</span>
                </td>
              </tr>
            ))}
            {data.ledger.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-sm text-anvi-500">No transactions yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {linkTarget && <LinkModal bm={linkTarget} siteMilestones={data.site_milestones} onClose={() => setLinkTarget(null)} onLink={link} />}
      {showCN && <CreditNoteModal projectId={projectId} onClose={() => setShowCN(false)} onCreated={() => { toast.success("Credit note issued"); load(); }} />}
    </div>
  );
}
